import { useEffect, useState } from 'react'

import { errorMessage } from '@/api/client'
import { useNiceClasses } from '@/api/corpusIntel'
import { useSaveClasses, type MatterClass } from '@/api/specification'
import { Button, Field, Input, Textarea, useToast } from '@/components/ui'

import {
  CLASS_RANGE_ERROR,
  CURRENT_TEXT_ERROR,
  draftErrors,
  emptyRow,
  toDraft,
  toInput,
  withProposal,
  withSourcedProposal,
  type DraftRow,
} from './specificationDraft'

/**
 * Recording the application's goods and services — the missing half of
 * `SpecificationCard`.
 *
 * ## Why this had to exist
 *
 * Every part of the amendment arm reads `matter_classes`: `draft_amend_v6`'s
 * `current_specification` block, the per-class Nice retrieval that fills
 * `nice_candidates`, the two VERIFY gate rules on proposed wording, and the
 * table on the Case screen. Until this component, nothing in the application
 * could write a single row. A specification matter created through New Case
 * therefore reached the drafter with an empty specification, and the card said
 * "Nothing on record for this application yet" beside no way to put anything on
 * record.
 *
 * ## What the picker is for
 *
 * `tm_nice_class` holds ~83,000 pre-approved descriptions and until now they
 * were only browsable, on a different screen, next to nothing that could use
 * them. Picking one here is what makes a proposal *sourced* — quotable wording
 * with an id behind it, rather than a sentence the drafter or the attorney
 * composed. The search is narrowed to the row's own class for the same reason
 * `amendment_inputs` ranks per class: offering a class 3 amendment class 25
 * wording is offering the applicant a different application.
 *
 * The rules governing the draft itself — chiefly that `nice_source_id` never
 * survives an edit — live in `specificationDraft.ts` with their reasons.
 */
export function SpecificationEditor({
  matterId,
  rows,
  onDone,
}: {
  matterId: string
  rows: MatterClass[]
  onDone: () => void
}) {
  const [draft, setDraft] = useState<DraftRow[]>(() => toDraft(rows))
  const [showErrors, setShowErrors] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const save = useSaveClasses(matterId)
  const toast = useToast()

  const errors = draftErrors(draft)
  // Nothing is red until somebody has tried to save. A form that flags an empty
  // row the instant it is added is scolding the attorney for the state it put
  // them in.
  const visible = showErrors ? errors : {}

  function update(key: string, next: (row: DraftRow) => DraftRow) {
    setDraft((current) => current.map((row) => (row.key === key ? next(row) : row)))
  }

  async function submit() {
    setFailure(null)
    if (Object.keys(errors).length > 0) {
      setShowErrors(true)
      return
    }
    try {
      const saved = await save.mutateAsync(toInput(draft))
      toast.push(saved.length === 1 ? '1 class recorded' : `${saved.length} classes recorded`, {
        tone: 'success',
        description:
          'The drafter now has the application’s own wording, and the gate can check a replacement against it.',
      })
      onDone()
    } catch (caught) {
      setFailure(errorMessage(caught))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        {draft.map((row) => (
          <EditableRow
            key={row.key}
            row={row}
            error={visible[row.key]}
            onChange={(next) => update(row.key, next)}
            onRemove={() => setDraft((current) => current.filter((r) => r.key !== row.key))}
          />
        ))}
      </div>

      {draft.length === 0 ? (
        <p className="m-0 text-body text-muted">
          No classes yet. Add one for each class the application claims — the numbers and the
          wording are on the filing receipt.
        </p>
      ) : null}

      {visible.form ? (
        <p role="alert" className="m-0 text-meta text-warn-fg">
          {visible.form}
        </p>
      ) : null}

      {failure ? (
        <p role="alert" className="m-0 text-meta text-warn-fg">
          {failure}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          onClick={() => setDraft((current) => [...current, emptyRow()])}
          disabled={save.isPending}
        >
          Add a class
        </Button>
        <span className="grow" />
        <Button variant="ghost" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} loading={save.isPending} loadingLabel="Saving…">
          Save specification
        </Button>
      </div>
    </div>
  )
}

function EditableRow({
  row,
  error,
  onChange,
  onRemove,
}: {
  row: DraftRow
  error: string | undefined
  onChange: (next: (row: DraftRow) => DraftRow) => void
  onRemove: () => void
}) {
  const [picking, setPicking] = useState(false)
  const classNumber = Number(row.classNumber)
  const pickable = Number.isInteger(classNumber) && classNumber >= 1 && classNumber <= 45

  return (
    <div className="flex flex-col gap-3 rounded-md border border-divider p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[120px_1fr]">
        <Field label="Class" required error={error === CLASS_RANGE_ERROR ? error : undefined}>
          {({ id, invalid }) => (
            <Input
              id={id}
              type="number"
              min={1}
              max={45}
              invalid={invalid}
              value={row.classNumber}
              onChange={(event) =>
                onChange((current) => ({ ...current, classNumber: event.target.value }))
              }
            />
          )}
        </Field>
        <Field
          label="Current wording"
          required
          hint="Exactly as the application stands today — this is what the examiner objected to."
          error={error === CURRENT_TEXT_ERROR ? error : undefined}
        >
          {({ id, invalid }) => (
            <Textarea
              id={id}
              rows={2}
              invalid={invalid}
              value={row.currentText}
              onChange={(event) =>
                onChange((current) => ({ ...current, currentText: event.target.value }))
              }
            />
          )}
        </Field>
      </div>

      <Field
        label="Proposed wording"
        hint={
          row.niceSourceId === null
            ? 'Leave this blank to let the drafter propose one. Anything typed here is yours, and the gate will flag it as wording it could not check.'
            : 'Taken verbatim from a pre-approved description. Editing it makes it yours again.'
        }
      >
        {({ id }) => (
          <Textarea
            id={id}
            rows={2}
            value={row.proposedText}
            onChange={(event) => onChange((current) => withProposal(current, event.target.value))}
          />
        )}
      </Field>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setPicking((open) => !open)}
          disabled={!pickable}
          title={pickable ? undefined : 'Enter a class number first — the list is per class.'}
        >
          {picking ? 'Hide pre-approved wording' : 'Find pre-approved wording'}
        </Button>
        <span className="grow" />
        <Button variant="ghost" size="sm" onClick={onRemove}>
          Remove class
        </Button>
      </div>

      {picking && pickable ? (
        <WordingPicker
          classNumber={classNumber}
          onPick={(description, id) => {
            onChange((current) => withSourcedProposal(current, description, id))
            setPicking(false)
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * Milliseconds of quiet before a typed search reaches the server. Matches
 * `IntelligenceScreen`'s own constant and for the same reason: `tm_nice_class`
 * is ~83,000 rows and `q` runs a COUNT plus a case-insensitive scan over all of
 * it, so a query per keystroke is twenty queries for one phrase.
 */
const SEARCH_DEBOUNCE_MS = 300

/**
 * How many descriptions the picker lists at once. Said on the screen when it
 * bites, because a silently truncated list reads as "that is all there is".
 */
const PICKER_LIMIT = 12

function WordingPicker({
  classNumber,
  onPick,
}: {
  classNumber: number
  onPick: (description: string, id: number) => void
}) {
  const [q, setQ] = useState('')
  const [submitted, setSubmitted] = useState('')
  const classes = useNiceClasses(classNumber, submitted.trim() || null, true)

  useEffect(() => {
    const timer = setTimeout(() => setSubmitted(q), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [q])

  const found = classes.data?.classes ?? []

  return (
    <div className="flex flex-col gap-2 rounded-md bg-ground p-3">
      <Field label={`Pre-approved wording in class ${classNumber}`} hint="Substring match">
        {({ id }) => <Input id={id} value={q} onChange={(event) => setQ(event.target.value)} />}
      </Field>

      {classes.isPending ? (
        <p className="m-0 text-meta text-muted">Looking…</p>
      ) : classes.isError ? (
        <p className="m-0 text-meta text-warn-fg">{errorMessage(classes.error)}</p>
      ) : found.length === 0 ? (
        <p className="m-0 text-meta text-muted">
          Nothing pre-approved matches that in class {classNumber}. Wording of your own is fine —
          the gate will say it could not check it, which is a true thing to tell a reviewer.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {found.slice(0, PICKER_LIMIT).map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                className="w-full rounded px-2 py-1 text-left text-meta hover:bg-divider"
                onClick={() => onPick(entry.description, entry.id)}
              >
                {entry.description}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* The server's own count, never a re-derivation from the page length.
          `/api/corpus/nice-classes` caps its array at 100 and reports the true
          match count separately, so "of {found.length}" says 100 when four
          thousand descriptions matched — and `api/corpusIntel.ts`'s own module
          docstring forbids exactly that re-derivation, because the server's cap
          is the only one honest about a query that matched more than it
          returned. Two numbers, because two different things are being
          truncated: this list shows PICKER_LIMIT of what arrived, and what
          arrived may itself be a capped subset of what matched. */}
      {found.length > PICKER_LIMIT ? (
        <p className="m-0 text-micro text-muted">
          Showing {PICKER_LIMIT} of the {found.length} returned. Narrow the search to see the
          rest.
        </p>
      ) : null}
      {classes.data?.capped ? (
        <p className="m-0 text-micro text-warn-fg">
          {classes.data.total} descriptions matched in class {classNumber} — more than this
          search returns. Narrow it before concluding the wording you want is not here.
        </p>
      ) : null}
    </div>
  )
}
